import classNames from 'classnames';
import { animate, useMotionValue } from 'framer-motion';
import type { MouseEventHandler } from 'react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from './Button';
import SVGIcon from './SVGIcon';
import * as styles from './Tabs.module.scss';
import caretIcon from '../icons/non-prefilled/16x16/caret.svg';
import Link from '../utils/router/Link';

type TabProps = {
  className?: string;
  activeClassName?: string;
  children: React.ReactNode;
  active?: boolean;
  onlyActiveOnIndex?: boolean;
  onlyActiveOnSearchMatch?: boolean;
} & (
  | {
      to: React.ComponentProps<typeof Link>['to'];
      onClick?: MouseEventHandler;
    }
  | { to?: never; onClick: MouseEventHandler }
);

export const Tab = ({
  className,
  activeClassName = '',
  children,
  to,
  onClick,
  active,
  onlyActiveOnIndex,
  onlyActiveOnSearchMatch,
}: TabProps) =>
  to ? (
    <Link
      onClick={onClick}
      to={to}
      className={classNames(styles.tab, className)}
      activeClassName={classNames(styles.active, activeClassName)}
      onlyActiveOnIndex={onlyActiveOnIndex}
      onlyActiveOnSearchMatch={onlyActiveOnSearchMatch}
    >
      {children}
    </Link>
  ) : (
    <Button
      onClick={onClick}
      className={classNames(styles.tab, className, {
        [styles.active]: active,
        [activeClassName]: active,
      })}
    >
      {children}
    </Button>
  );

// Width of caret which needs to be subtracted when scrolling
const CARET_OFFSET = 28;

const PAGINATION_OFFSET = 2 * CARET_OFFSET + 10;

interface TabsProps {
  children: React.ReactNode;
  right?: React.ReactNode;
}

const Tabs = ({ children, right }: TabsProps) => {
  const [{ scrollWidth, clientWidth, scrollLeft }, setScrollData] = useState({
    scrollWidth: 0,
    clientWidth: 0,
    scrollLeft: 0,
  });
  const [isForceHidingLeftShadow, setIsForceHidingLeftShadow] = useState(false);
  const [isForceHidingRightShadow, setIsForceHidingRightShadow] =
    useState(false);

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const x = useMotionValue(0);

  const isScrollable = scrollWidth > clientWidth;
  const isLeftMost = scrollLeft === 0;
  const isRightMost = scrollWidth - clientWidth - scrollLeft <= 0; // Could be slightly below zero because of some franctions of pixel

  const shouldShowLeftShadow = !isLeftMost && !isForceHidingLeftShadow;
  const shouldShowRightShadow = !isRightMost && !isForceHidingRightShadow;

  const resetScrollData = useCallback(() => {
    if (!scrollContainerRef.current) {
      return;
    }

    x.set(scrollContainerRef.current.scrollLeft);

    setScrollData({
      scrollWidth: scrollContainerRef.current.scrollWidth,
      clientWidth: scrollContainerRef.current.clientWidth,
      scrollLeft: scrollContainerRef.current.scrollLeft,
    });
    setIsForceHidingLeftShadow(false);
    setIsForceHidingRightShadow(false);
  }, [x]);

  useEffect(() => resetScrollData(), [children, resetScrollData]);

  const animateScrollTo = (target: number, onComplete?: () => void) => {
    animate(x, target, {
      type: 'tween',
      ease: [0.36, 0.24, 0.59, 1],
      duration: 0.3,
      onUpdate: (value) => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollLeft = value;
        }
      },
      onComplete: () => {
        resetScrollData();
        if (onComplete) {
          onComplete();
        }
      },
    });
  };

  const onChildClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const { offsetLeft, offsetWidth } = e.currentTarget;

    // Is too far left
    if (scrollLeft >= offsetLeft - CARET_OFFSET) {
      animateScrollTo(offsetLeft - CARET_OFFSET, () => {
        setIsForceHidingLeftShadow(true);
      });
      return;
    }

    const elementRightCorner = offsetLeft + offsetWidth;
    const visibleRightCorner = scrollLeft + clientWidth;

    // Is too far right
    if (elementRightCorner + CARET_OFFSET >= visibleRightCorner) {
      animateScrollTo(elementRightCorner - clientWidth + CARET_OFFSET, () => {
        setIsForceHidingRightShadow(true);
      });
    }
  };

  const handleScrollRight = () => {
    if (!isScrollable || isRightMost) {
      return;
    }

    animateScrollTo(
      Math.min(
        scrollLeft + clientWidth - PAGINATION_OFFSET,
        scrollWidth - clientWidth,
      ),
    );
  };

  const handleScrollLeft = () => {
    if (!isScrollable || isLeftMost) {
      return;
    }

    animateScrollTo(Math.max(scrollLeft - clientWidth + PAGINATION_OFFSET, 0));
  };

  return (
    <div className={styles.container}>
      <div
        className={classNames(styles.scrollContainer, {
          [styles.withRight]: right,
        })}
        onScroll={() => !x.isAnimating() && resetScrollData()}
        ref={scrollContainerRef}
      >
        <div className={styles.tabs}>
          {React.Children.map(children, (child) => (
            <div onClick={onChildClick}>{child}</div>
          ))}
        </div>
        {right}
      </div>
      {isScrollable && !isLeftMost && (
        <div
          className={classNames(styles.scrollHint, styles.left, {
            [styles.disabledShadow]: !shouldShowLeftShadow,
          })}
        >
          <div className={styles.scrollCaret} onClick={handleScrollLeft}>
            <SVGIcon inline size="small">
              {caretIcon}
            </SVGIcon>
          </div>
        </div>
      )}
      {isScrollable && !isRightMost && (
        <div
          className={classNames(styles.scrollHint, styles.right, {
            [styles.disabledShadow]: !shouldShowRightShadow,
          })}
        >
          <div className={styles.scrollCaret} onClick={handleScrollRight}>
            <SVGIcon inline size="small">
              {caretIcon}
            </SVGIcon>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tabs;
